/**
 * @mention (people) suggestion configuration for the TipTap Mention extension.
 *
 * - Trigger: `@`
 * - API: GET /api/people/search?q=<query>
 * - Inserts an inline mention node with the person's id and name
 */

import { PluginKey } from '@tiptap/pm/state';
import type { SuggestionOptions } from '@tiptap/suggestion';
import { apiGet } from '../../lib/api';
import { createSuggestionRenderer } from './suggestion-renderer';
import type { SuggestionItem } from './SuggestionList';

// API response shape from GET /api/people/search
interface PersonSearchResult {
  id: string;
  name: string;
  email?: string;
  role?: string;
}

export const MentionPluginKey = new PluginKey('mentionPeople');

export const mentionSuggestion: Omit<SuggestionOptions<SuggestionItem>, 'editor'> = {
  char: '@',
  pluginKey: MentionPluginKey,
  allowSpaces: true,

  items: async ({ query }) => {
    if (!query || query.length === 0) {
      // Fetch recent / all people when the user just types "@"
      try {
        const results = await apiGet<PersonSearchResult[]>('/people/search?q=');
        return results.map(personToItem);
      } catch {
        return [];
      }
    }

    try {
      const results = await apiGet<PersonSearchResult[]>(
        `/people/search?q=${encodeURIComponent(query)}`,
      );
      return results.map(personToItem);
    } catch {
      return [];
    }
  },

  render: createSuggestionRenderer({ variant: 'mention' }),
};

function personToItem(person: PersonSearchResult): SuggestionItem {
  return {
    id: person.id,
    label: person.name,
    secondary: person.email || person.role || undefined,
  };
}
